import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { Column, Row } from 'Core/components/Layout';
import InputField from 'Core/components/FormElements/InputField';
import Error from 'Core/components/FormElements/Error';
import QuestionMark from 'Core/components/QuestionMark';
import { AlertError } from 'Core/components/Alert';
import { getFormPrefix } from 'Core/utils/utils';

// Modern Stripe element styling
const modernStripeConfig = {
  style: {
    base: {
      color: '#1f2937',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSize: '16px',
      fontWeight: '400',
      '::placeholder': {
        color: '#9ca3af',
      },
      lineHeight: '24px',
    },
    invalid: {
      color: '#dc2626',
      iconColor: '#dc2626',
    },
  },
};

// Modern Stripe input wrapper
const StripeInputWrapper = ({ label, htmlFor, error, children, tooltip }) => (
  <div className="flex flex-col gap-1.5 w-full">
    <label
      htmlFor={htmlFor}
      className="text-sm font-medium text-gray-700 flex items-center gap-1 whitespace-nowrap"
    >
      {label}
      {tooltip}
    </label>
    <div
      className={`
        h-11 px-3 py-2 rounded-lg border transition-all duration-200 bg-white
        ${
          error
            ? 'border-red-300 ring-2 ring-red-100'
            : 'border-gray-300 hover:border-gray-400 focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20'
        }
      `}
    >
      {children}
    </div>
  </div>
);

const NewCard = ({
  index = 0,
  form,
  namePrefix = '',
  children = null,
  cardError = '',
  defaultValues = {},
  shouldShowHeader = false,
}) => {
  const { t } = useTranslation();
  const stripeElements = useElements();
  const stripe = useStripe();
  const {
    register,
    unregister,
    setError,
    setValue,
    clearErrors,
    formState: { errors },
  } = form;

  const prefix = getFormPrefix(namePrefix, index);
  const { firstName, lastName, postalCode } = defaultValues;
  const validationErrors = namePrefix ? errors?.[namePrefix]?.[index] : errors;

  const handleStripeElementChange = (event, name) => {
    const { error: stripeError } = event;
    if (stripeError) {
      setError(name, { type: 'manual', message: t(`stripe.${stripeError.code}`) });
      return;
    }
    setValue(name, '1');
    clearErrors(name);
  };

  React.useEffect(() => {
    if (stripe && stripeElements) {
      setValue(`${prefix}stripeElement`, stripeElements.getElement(CardNumberElement));
      setValue(`${prefix}stripe`, stripe);
    }

    return () => unregister([`${prefix}cardNumber`, `${prefix}cardExpiry`, `${prefix}cardCvc`]);
  }, [setValue, unregister, prefix, stripe, stripeElements]);

  return (
    <div className="space-y-5">
      {shouldShowHeader && (
        <h3
          data-testid={`card-information-headline-${index}`}
          className="text-lg font-semibold text-gray-900"
        >
          {t('common.cardInformation')}
        </h3>
      )}
      {cardError && <AlertError className="mb-4">{cardError}</AlertError>}

      {/* Section Label */}
      <p className="text-md font-bold text-gray-600 mb-2">{t('common.enterNewCardDetails')}</p>

      {/* Cardholder Name - Two columns on desktop */}
      <Row>
        <Column className="!flex-col-12 md:!flex-col-6">
          <InputField
            data-testid="firstName"
            defaultValue={firstName}
            register={register}
            label={t('common.cardHolderFirstName')}
            name={`${prefix}firstName`}
            error={validationErrors?.firstName?.message}
          />
        </Column>
        <Column className="!flex-col-12 md:!flex-col-6">
          <InputField
            data-testid="lastName"
            defaultValue={lastName}
            register={register}
            label={t('common.cardHolderLastName')}
            name={`${prefix}lastName`}
            error={validationErrors?.lastName?.message}
          />
        </Column>
      </Row>

      {/* Card Details - Responsive grid */}
      <Row>
        <Column className="!flex-col-12 md:!flex-col-5 lg:!flex-col-4">
          <StripeInputWrapper
            label={t('common.cardNumber')}
            htmlFor={`cardNumber${index}`}
            error={validationErrors?.cardNumber?.message}
          >
            <CardNumberElement
              id={`cardNumber${index}`}
              options={modernStripeConfig}
              onChange={(e) => handleStripeElementChange(e, `${prefix}cardNumber`)}
            />
          </StripeInputWrapper>
          {validationErrors?.cardNumber?.message && (
            <Error message={t(validationErrors?.cardNumber?.message)} show={true} />
          )}
        </Column>
        <Column className="!flex-col-6 md:!flex-col-2">
          <StripeInputWrapper
            label={t('common.cardExpiry')}
            htmlFor={`expiryDate${index}`}
            error={validationErrors?.cardExpiry?.message}
          >
            <CardExpiryElement
              id={`expiryDate${index}`}
              options={modernStripeConfig}
              onChange={(e) => handleStripeElementChange(e, `${prefix}cardExpiry`)}
            />
          </StripeInputWrapper>
          {validationErrors?.cardExpiry?.message && (
            <Error message={t(validationErrors?.cardExpiry?.message)} show={true} />
          )}
        </Column>
        <Column className="!flex-col-6 md:!flex-col-3">
          <StripeInputWrapper
            label={t('common.cardCode')}
            htmlFor={`cvc${index}`}
            error={validationErrors?.cardCvc?.message}
            tooltip={<QuestionMark text={t('common.cardCodeInfo')} />}
          >
            <CardCvcElement
              id={`cvc${index}`}
              data-testid="cvc"
              options={modernStripeConfig}
              onChange={(e) => handleStripeElementChange(e, `${prefix}cardCvc`)}
            />
          </StripeInputWrapper>
          {validationErrors?.cardCvc?.message && (
            <Error message={t(validationErrors?.cardCvc?.message)} show={true} />
          )}
        </Column>
        <Column className="!flex-col-12 md:!flex-col-3">
          <InputField
            data-testid="postalCode"
            defaultValue={postalCode}
            register={register}
            registerOptions={{
              setValueAs: (v) => (!v ? '' : String(v)),
            }}
            type="number"
            min="0"
            inputMode="numeric"
            pattern="[0-9]*"
            label={t('common.postalCode')}
            name={`${prefix}postalCode`}
            error={validationErrors?.postalCode?.message}
          />
        </Column>
      </Row>
      {children}
    </div>
  );
};

export default NewCard;
